import { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  :root {
    --cream:      #F7F3EE;
    --cream-dark: #EDE7DC;
    --warm-tan:   #C9AA8C;
    --terracotta: #B87355;
    --mocha:      #7A5C47;
    --ink:        #2E1F14;
    --ink-soft:   #5C4033;
    --gold:       #C49A6C;
    --font-serif: 'Cormorant Garamond', Georgia, serif;
    --font-sans:  'Jost', sans-serif;
  }
  html { scroll-behavior: smooth; }
  body {
    background: var(--cream);
    color: var(--ink);
    font-family: var(--font-sans);
    font-weight: 300;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
`;

// ─── Data ──────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "event",
    label: "01",
    title: "Event",
    titleItalic: "Make-up",
    tagline: "Für besondere Anlässe & Abende",
    note: "Termine ab 15:00 Uhr · Hairstyling nur in Kombination mit Make-up buchbar",
    packages: [
      {
        name: "Abend Make-up",
        price: "120€",
        includes: [
          "Typgerechtes, langanhaltendes Make-up",
          "Modern, elegant oder soft Glam",
          "Abgestimmt auf Anlass, Outfit & Gesamtlook",
        ],
      },
      {
        name: "Abend Make-up & Hairstyling",
        price: "200€",
        includes: [
          "Kompletter Look – harmonisch abgestimmt",
          "Make-up & Hairstyling aus einer Hand",
          "Ready für den Abend",
        ],
      },
    ],
  },
  {
    id: "shooting",
    label: "02",
    title: "Shooting",
    titleItalic: "Styling",
    tagline: "Für Shootings, Content Creation & Produktionen",
    note: "Mindestbuchung 2 Stunden · Fahrtkosten außerhalb Münchens individuell · Ganztages- & Projektbuchungen auf Anfrage",
    packages: [
      {
        name: "Stundensatz",
        price: "120€ / h",
        includes: [
          "Kameraoptimiertes Make-up & Styling",
          "Abgestimmt auf Licht, Konzept & Ästhetik",
          "Für Personal Shootings bis Brand-Kampagnen",
        ],
      },
      {
        name: "Ab 5 Stunden",
        price: "100€ / h",
        includes: [
          "Vergünstigter Stundensatz",
          "Ideal für Ganztages-Produktionen",
          "Flexibel buchbar",
        ],
      },
    ],
  },
  {
    id: "workshop",
    label: "03",
    title: "Make-up",
    titleItalic: "Workshop",
    tagline: "Gruppen-Erlebnis · Geburtstag · JGA · Freundinnen",
    note: "Mindestteilnehmerzahl 5 Personen · ca. 3 Stunden · Studio oder bei euch vor Ort · Professionelle Produkte & Erfrischungen inklusive",
    packages: [
      {
        name: "Pro Person",
        price: "100€",
        includes: [
          "Individuelle Tipps für dein Gesicht & deinen Hauttyp",
          "Schritt-für-Schritt Anleitung für deinen persönlichen Look",
          "Alltagstaugliche Techniken",
          "Produktempfehlungen von High-End bis erschwinglich",
        ],
      },
    ],
  },
  {
    id: "schulung",
    label: "04",
    title: "1:1",
    titleItalic: "Schulung",
    tagline: "Für angehende & tätige Make-up Artists",
    note: "Durchführung in meinem Studio · Termine auf Anfrage · Inkl. Zertifikat, Goodie Bag, E-Book & Verpflegung",
    packages: [
      {
        name: "1 Tag",
        price: "690€",
        includes: [
          "Technik, Hygiene & professioneller Produktumgang",
          "Hautvorbereitung, Haltbarkeit & typgerechtes Arbeiten",
          "Einblicke in reale Kundenabläufe",
          "Zertifikat · Goodie Bag · E-Book · Verpflegung",
        ],
      },
      {
        name: "2 Tage",
        price: "1.190€",
        includes: [
          "Vertiefung aller Inhalte über zwei volle Tage",
          "Individuell abgestimmt auf deinen Stand & deine Ziele",
          "Zertifikat · Goodie Bag · E-Book · Verpflegung",
        ],
        highlight: true,
      },
    ],
  },
  {
    id: "bridal",
    label: "05",
    title: "Bridal",
    titleItalic: "Styling",
    tagline: "Hochzeit · Standesamt · Henna · Verlobung",
    note: "Anfahrt innerhalb Münchens: 30€ pauschal · Außerhalb: 0,50€/km",
    packages: [
      {
        name: "Bridal Make-up",
        price: "280€",
        includes: [
          "Braut Make-up am Hochzeitstag",
          "Individuell abgestimmt (ohne Hairstyling)",
          "Langanhaltendes, fototaugliches Finish",
          "Touch-up Kit",
        ],
      },
      {
        name: "Standesamt Styling",
        price: "400€",
        includes: [
          "Make-up & Hairstyling",
          "Natürliches bis soft Glam Finish",
          "Für besondere Anlässe im kleineren Rahmen",
        ],
      },
      {
        name: "Bridal Experience",
        price: "450€",
        includes: [
          "Make-up & Hairstyling am Hochzeitstag",
          "Langanhaltendes, fototaugliches Finish",
          "Touch-up Kit",
        ],
      },
      {
        name: "Bridal Experience Signature",
        price: "700€",
        highlight: true,
        includes: [
          "Make-up & Hairstyling am Hochzeitstag",
          "Probetermin im Studio (Make-up & Hairstyling, 2–3 Std.)",
          "Individuelle Abstimmung deines finalen Looks",
          "Touch-up Kit",
        ],
      },
      {
        name: "Bridal Experience Plus",
        price: "1.100€",
        includes: [
          "Styling für zwei Termine (z. B. Standesamt & Hochzeit)",
          "Probetermin im Studio",
          "Individuelle Abstimmung beider Looks",
          "Touch-up Kit",
        ],
      },
      {
        name: "Bridal Full Day",
        price: "1.300€",
        includes: [
          "Make-up & Hairstyling + Probetermin",
          "Ganztägige Begleitung bis zu 12 Stunden",
          "Auffrischen über den Tag · optionales Umstyling",
          "Touch-up Kit",
        ],
      },
    ],
  },
  {
    id: "destination",
    label: "06",
    title: "Destination",
    titleItalic: "Bridal",
    tagline: "Hochzeit im Ausland · Europa & weltweit",
    note: "Kontaktiere mich per WhatsApp oder E-Mail mit deinen Hochzeitsdetails — ich erstelle dir ein individuelles Angebot",
    packages: [
      {
        name: "Individuelles Angebot",
        price: "Auf Anfrage",
        includes: [
          "Persönliche Begleitung an deinem Hochzeitsort",
          "Abgestimmt auf Destination, Kleid & Gesamtkonzept",
          "Preis je nach Reiseziel, Dauer & Stylingumfang",
        ],
      },
    ],
  },
];

// ─── Styled ────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: var(--cream);
`;

const Hdr = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  padding: 1.8rem 5vw;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(247, 243, 238, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(122, 92, 71, 0.08);
`;

const LogoA = styled.a`
  font-family: var(--font-serif);
  font-size: 1rem;
  font-weight: 300;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--ink);
  text-decoration: none;
`;

const BackA = styled.a`
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-soft);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  transition: color 0.3s;
  &:hover {
    color: var(--terracotta);
  }
  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(-3px);
  }
`;

// ── Hero ───────────────────────────────────────────────────────────────
const Hero = styled.div`
  padding: 13rem 5vw 6rem;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 4rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    padding-top: 10rem;
  }
`;

const HeroLeft = styled.div``;

const HWrap = styled.div`
  overflow: hidden;
  margin-bottom: 0.05rem;
`;
const BigH = styled(motion.h1)`
  font-family: var(--font-serif);
  font-size: clamp(4rem, 8vw, 9rem);
  font-weight: 300;
  line-height: 0.88;
  color: var(--ink);
  letter-spacing: -0.025em;
`;
const BigHItalic = styled(BigH)`
  font-style: italic;
  color: var(--terracotta);
`;

const HeroRight = styled(motion.div)`
  padding-bottom: 0.5rem;
`;
const HeroSub = styled.p`
  font-family: var(--font-sans);
  font-size: 0.82rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.9;
  color: var(--ink-soft);
  margin-bottom: 2rem;
  max-width: 36ch;
`;
const HeroDivider = styled(motion.div)`
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, var(--terracotta), var(--gold));
  opacity: 0.5;
  margin-bottom: 2rem;
`;
const HeroMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;
const HeroMetaRow = styled.span`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--warm-tan);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  &::before {
    content: "";
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--terracotta);
    opacity: 0.6;
    flex-shrink: 0;
  }
`;

const PageRule = styled(motion.div)`
  height: 1px;
  margin: 0 5vw;
  background: rgba(122, 92, 71, 0.12);
  transform-origin: left;
`;

// ── Sections list ──────────────────────────────────────────────────────
const SectionsList = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5vw 10rem;
`;

const SectionBlock = styled(motion.div)`
  padding: 5rem 0;
  border-bottom: 1px solid rgba(122, 92, 71, 0.08);
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTop = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 3rem;
  align-items: start;
  margin-bottom: 3.5rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SectionNum = styled(motion.span)`
  font-family: var(--font-serif);
  font-size: 0.75rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(184, 115, 85, 0.4);
  padding-top: 0.4rem;
  display: block;
`;

const SectionHeadRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionHeadWrap = styled.div``;
const SectionHead = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2rem, 3.5vw, 3.5rem);
  font-weight: 300;
  line-height: 0.9;
  color: var(--ink);
  letter-spacing: -0.015em;
`;
const SectionHeadItalic = styled(SectionHead)`
  font-style: italic;
  color: var(--terracotta);
`;
const SectionTagline = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 200;
  letter-spacing: 0.12em;
  color: var(--warm-tan);
  margin-top: 0.9rem;
  text-transform: uppercase;
`;

// ── Packages ───────────────────────────────────────────────────────────
const PackagesList = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 3rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PackagesInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PackageRow = styled(motion.div)`
  border-top: 1px solid rgba(122, 92, 71, 0.1);
  padding: 1.8rem 0;
  cursor: pointer;

  &:last-child {
    border-bottom: 1px solid rgba(122, 92, 71, 0.1);
  }
`;

const PackageRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
`;

const PackageLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex: 1;
`;

const HighlightDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--terracotta);
  flex-shrink: 0;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
`;

const PackageName = styled.span`
  font-family: var(--font-sans);
  font-size: clamp(0.82rem, 1.1vw, 0.95rem);
  font-weight: 300;
  letter-spacing: 0.06em;
  color: var(--ink);
`;

const PackageRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const PackagePrice = styled.span`
  font-family: var(--font-serif);
  font-size: clamp(1.4rem, 2vw, 1.9rem);
  font-weight: 300;
  color: var(--ink);
  letter-spacing: -0.01em;
  white-space: nowrap;
  padding-bottom: 0.05em;
  display: inline-block;
`;

const ExpandBtn = styled(motion.div)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(122, 92, 71, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--warm-tan);
  flex-shrink: 0;
  transition:
    border-color 0.3s,
    color 0.3s;

  ${PackageRow}:hover & {
    border-color: var(--terracotta);
    color: var(--terracotta);
  }
`;

const PackageDetails = styled(motion.div)`
  overflow: visible;
`;

const PackageDetailsInner = styled.div`
  padding: 1.2rem 0 0.4rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const IncludeItem = styled.span`
  font-family: var(--font-sans);
  font-size: 0.7rem;
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.7;
  color: var(--ink-soft);
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  &::before {
    content: "—";
    color: rgba(184, 115, 85, 0.4);
    flex-shrink: 0;
    font-weight: 300;
  }
`;

// ── Section note ───────────────────────────────────────────────────────
const SectionNote = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 3rem;
  margin-top: 2rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const NoteText = styled.p`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.8;
  color: rgba(122, 92, 71, 0.5);
  font-style: italic;
`;

// ── CTA ────────────────────────────────────────────────────────────────
const CtaWrap = styled(motion.div)`
  background: var(--ink);
  padding: 8rem 5vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2rem;
`;

const CtaHead = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(1.8rem, 3.5vw, 3.2rem);
  font-weight: 300;
  font-style: italic;
  color: var(--cream);
  line-height: 1.2;
`;

const CtaSub = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.78rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.85;
  color: rgba(247, 243, 238, 0.35);
  max-width: 36ch;
`;

const CtaRow = styled(motion.div)`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const CtaBtn = styled(motion.a)`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 300;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 1rem 2.4rem;
  background: ${({ $primary }) =>
    $primary ? "var(--terracotta)" : "transparent"};
  color: var(--cream);
  border: 1px solid
    ${({ $primary }) =>
      $primary ? "var(--terracotta)" : "rgba(247,243,238,0.18)"};
  position: relative;
  overflow: visible;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $primary }) =>
      $primary ? "var(--mocha)" : "rgba(247,243,238,0.05)"};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }
`;

// ─── Package row with expand ───────────────────────────────────────────
function PkgRow({ pkg, index }) {
  const [open, setOpen] = useState(false);
  return (
    <PackageRow
      key={pkg.name}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.05 * index,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={() => setOpen((o) => !o)}
    >
      <PackageRowTop>
        <PackageLeft>
          <HighlightDot $show={pkg.highlight} />
          <PackageName>{pkg.name}</PackageName>
        </PackageLeft>
        <PackageRight>
          <PackagePrice>{pkg.price}</PackagePrice>
          <ExpandBtn
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M5 1v8M1 5h8"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </ExpandBtn>
        </PackageRight>
      </PackageRowTop>

      <AnimatePresence initial={false}>
        {open && (
          <PackageDetails
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <PackageDetailsInner>
              {pkg.includes.map((item, i) => (
                <IncludeItem key={i}>{item}</IncludeItem>
              ))}
            </PackageDetailsInner>
          </PackageDetails>
        )}
      </AnimatePresence>
    </PackageRow>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────
export default function PricingPage() {
  const stagger = (delay = 0) => ({
    hidden: {},
    show: { transition: { staggerChildren: 0.09, delayChildren: delay } },
  });

  const revealUp = {
    hidden: { y: "105%" },
    show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Pricing - Laureta Martini";
  }, []);

  return (
    <>
      <GlobalStyle />
      <Page>
        {/* Header */}

        {/* Hero */}
        <Hero>
          <HeroLeft>
            <HWrap>
              <BigH
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 0.9,
                  delay: 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                Preise &
              </BigH>
            </HWrap>
            <HWrap>
              <BigHItalic
                as={motion.h1}
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 0.9,
                  delay: 0.27,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                Pakete.
              </BigHItalic>
            </HWrap>
          </HeroLeft>

          <HeroRight
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <HeroSub>
              Alle Preise sind Richtpreise. Für individuelle Anfragen und
              maßgeschneiderte Pakete bin ich jederzeit erreichbar.
            </HeroSub>
            <HeroDivider
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.65, duration: 0.7 }}
              style={{ transformOrigin: "left" }}
            />
            <HeroMeta>
              <HeroMetaRow>München & Umgebung</HeroMetaRow>
              <HeroMetaRow>Auf Anfrage deutschlandweit</HeroMetaRow>
              <HeroMetaRow>Destination Bridal weltweit</HeroMetaRow>
            </HeroMeta>
          </HeroRight>
        </Hero>

        <PageRule
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Sections */}
        <SectionsList>
          {SECTIONS.map((sec, si) => (
            <SectionBlock
              key={sec.id}
              id={sec.id}
              initial="hidden"
              animate="show"
              variants={stagger(0.5 + si * 0.06)}
            >
              <SectionTop>
                <SectionNum variants={fadeUp}>{sec.label}</SectionNum>

                <SectionHeadRow>
                  <SectionHeadWrap>
                    <SectionHead variants={revealUp}>{sec.title}</SectionHead>
                  </SectionHeadWrap>
                  <SectionHeadWrap>
                    <SectionHeadItalic as={motion.h2} variants={revealUp}>
                      {sec.titleItalic}
                    </SectionHeadItalic>
                  </SectionHeadWrap>
                  <SectionTagline variants={fadeUp}>
                    {sec.tagline}
                  </SectionTagline>
                </SectionHeadRow>
              </SectionTop>

              {/* Package rows */}
              <PackagesList variants={fadeUp}>
                <div /> {/* spacer for grid alignment */}
                <PackagesInner>
                  {sec.packages.map((pkg, pi) => (
                    <PkgRow key={pkg.name} pkg={pkg} index={pi} />
                  ))}
                </PackagesInner>
              </PackagesList>

              {/* Note */}
              {sec.note && (
                <SectionNote variants={fadeUp}>
                  <div />
                  <NoteText>{sec.note}</NoteText>
                </SectionNote>
              )}
            </SectionBlock>
          ))}
        </SectionsList>

        {/* CTA */}
        {/* <CtaWrap
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.9 }}
        >
          <CtaHead
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            Bereit zu buchen?
          </CtaHead>
          <CtaSub
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            Schreib mir auf WhatsApp oder Instagram — ich melde mich innerhalb
            von 24 Stunden.
          </CtaSub>
          <CtaRow
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
          >
            <CtaBtn
              $primary
              href="https://wa.me/4915000000000"
              target="_blank"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>WhatsApp</span>
            </CtaBtn>
            <CtaBtn
              href="https://instagram.com/lenamueller.beauty"
              target="_blank"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>Instagram</span>
            </CtaBtn>
          </CtaRow>
        </CtaWrap> */}
      </Page>
    </>
  );
}
